import { z } from "zod";

/** Sanitised clinic info safe to show to anyone with the public URL. */
export interface PublicClinicInfo {
	slug: string;
	name: { ar: string; en?: string };
	business_type: string;
	default_language: "ar" | "en";
	rtl_default: boolean;
	branding: {
		logo_url: string | null;
		primary_color: string | null;
		accent_color: string | null;
		cover_image_url: string | null;
	};
	bio: { ar: string | null; en: string | null };
	is_accepting_bookings: boolean;
	/** Optional public contact block (phone / address / map / hours note). */
	contact: {
		phone: string | null;
		address: string | null;
		maps_url: string | null;
		hours_note: string | null;
	} | null;
}

export interface PublicClinicService {
	id: string;
	name: { ar: string; en?: string };
	description: { ar?: string; en?: string } | null;
	duration_minutes: number;
	price: string;
	currency: string;
	category: string | null;
	color: string | null;
}

export const publicBookingInputSchema = z.object({
	full_name: z.string().trim().min(2).max(120),
	phone: z.string().trim().min(6).max(40),
	preferred_language: z.enum(["ar", "en"]).default("ar"),
	service_id: z.string().uuid().optional(),
	scheduled_at: z.string().datetime(),
	duration_minutes: z.number().int().positive().max(480).default(30),
	notes: z.string().max(1000).optional(),
});
export type PublicBookingInput = z.infer<typeof publicBookingInputSchema>;

export const publicSlotsQuerySchema = z.object({
	date: z.string().date(),
	duration_minutes: z.coerce.number().int().positive().max(480).default(30),
	service_id: z.string().uuid().optional(),
});
export type PublicSlotsQuery = z.infer<typeof publicSlotsQuerySchema>;

/**
 * Day-level availability for the booking wizard: which of the next N
 * days actually have at least one open slot. Lets the client jump
 * straight to the first bookable day instead of making the patient hunt.
 */
export const publicAvailabilityDaysQuerySchema = z.object({
	from: z.string().date(),
	days: z.coerce.number().int().min(1).max(21).default(14),
	duration_minutes: z.coerce.number().int().positive().max(480).default(30),
	service_id: z.string().uuid().optional(),
});
export type PublicAvailabilityDaysQuery = z.infer<
	typeof publicAvailabilityDaysQuerySchema
>;

export interface PublicAvailabilityDay {
	date: string; // YYYY-MM-DD
	has_slots: boolean;
}

export interface PublicAvailabilityDaysResponse {
	from: string;
	days: PublicAvailabilityDay[];
	/** First date in the range with at least one open slot, or null. */
	first_available_date: string | null;
}

export interface PublicBookingResult {
	appointment_id: string;
	scheduled_at: string;
	// NOTE: deliberately NO patient_created flag. Returning whether the phone
	// matched an existing patient would turn this unauthenticated endpoint into
	// a patient-existence oracle (submit a phone → learn it belongs to a patient
	// of this — possibly sensitive — clinic). The clinic still learns it
	// internally via the audit row + staff notification metadata.
	/** Display name of the doctor the booking landed on (for confirmation). */
	doctor_name: string | null;
	/** Pre-built confirmation hint for the success screen. */
	confirmation_hint: { ar: string; en: string };
}
